import { zodResolver } from '@hookform/resolvers/zod';
import { useQueryClient } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { useEffect, useId } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';

import type { TutorResponse } from '@/api/generated/model';
import {
  getTutoresControllerListarQueryKey,
  getTutoresControllerObterQueryKey,
  useTutoresControllerAtualizar,
  useTutoresControllerCriar,
} from '@/api/generated/tutores/tutores';
import { aplicarErrosDoServidor } from '@/api/erros';
import { CANAIS, ROTULO_CANAL } from '@/features/tutores/rotulos';
import { Campo, CLASSE_CONTROLE } from '@/shared/components/Campo';
import { CampoDeTelefone } from '@/shared/components/CampoDeTelefone';
import { CamposDeEndereco } from '@/shared/components/CamposDeEndereco';
import { telefoneValido } from '@/shared/lib/telefone';
import { enderecoFormSchema, enderecoParaEnvio, enderecoParaFormulario } from '@/shared/lib/endereco';
import { bloquearEnvioPorEnter } from '@/shared/lib/formulario';
import { Alert, AlertDescription } from '@/shared/ui/alert';
import { Button } from '@/shared/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog';
import { Input } from '@/shared/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/ui/select';
import { toast } from '@/shared/ui/sonner';

/** O `<Select>` do Radix não aceita `value=""`. */
const SEM_CANAL = 'nenhum';

const schema = z.object({
  nome: z.string().trim().min(3, 'Informe um nome com ao menos 3 caracteres').max(200),
  documento: z.string().trim().max(20),
  email: z.union([z.literal(''), z.email('E-mail inválido').max(200)]),
  /**
   * E.164, vindo do `CampoDeTelefone` — nunca o texto mascarado.
   *
   * O campo emite E.164 PARCIAL enquanto se digita, e é este refine que
   * transforma "número pela metade" em erro de tela. Sem ele, `+5511999` seria
   * gravado como se fosse um telefone.
   */
  telefone: z
    .string()
    .max(20)
    .refine((v) => v === '' || telefoneValido(v), 'Telefone incompleto para o país escolhido'),
  canalPreferido: z.string(),
  observacoes: z.string().trim().max(2000),
  /** Sai no receituário de controle especial (bloco do proprietário). */
  endereco: enderecoFormSchema,
});

type Formulario = z.infer<typeof schema>;

interface Props {
  aberto: boolean;
  aoFechar: () => void;
  /** Ausente = criação. */
  tutor?: TutorResponse;
  /**
   * Avisa quem abriu o diálogo QUAL tutor acabou de ser criado.
   *
   * Existe para o `SeletorDeTutor` já deixar o tutor novo escolhido no
   * formulário do paciente. Sem isto, quem clica no `+` cadastra o tutor,
   * volta para o formulário e tem de procurá-lo na lista — o cadastro
   * aconteceu justamente porque ele não estava lá.
   */
  aoCriar?: (tutor: TutorResponse) => void;
  /**
   * Avisa quem abriu o diálogo como o tutor ficou depois da edição.
   *
   * Existe para o `SeletorDeTutor`: o gatilho mostra o nome guardado na
   * escolha, e sem isto um nome corrigido só apareceria ao reabrir o paciente.
   */
  aoAtualizar?: (tutor: TutorResponse) => void;
}

export function TutorFormDialogo({ aberto, aoFechar, tutor, aoCriar, aoAtualizar }: Props) {
  const queryClient = useQueryClient();
  const criar = useTutoresControllerCriar();
  const atualizar = useTutoresControllerAtualizar();
  const editando = Boolean(tutor);

  /**
   * ⚠️ Ids de campo GERADOS, não literais.
   *
   * Este diálogo abre por dentro do formulário do paciente (o `+` do
   * `SeletorDeTutor`), e lá já existem `#nome` e `#observacoes`. Com id repetido
   * no documento, `<label for>` resolve para o PRIMEIRO elemento — clicar em
   * "Nome" aqui focava o nome do animal, no formulário de trás.
   */
  const prefixo = useId();
  const idDe = (campo: string) => `${prefixo}-${campo}`;

  const {
    register,
    control,
    handleSubmit,
    reset,
    setValue,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<Formulario>({ resolver: zodResolver(schema) });

  useEffect(() => {
    if (!aberto) return;

    reset({
      nome: tutor?.nome ?? '',
      documento: tutor?.documento ?? '',
      email: tutor?.email ?? '',
      telefone: tutor?.telefone ?? '',
      canalPreferido: tutor?.canalPreferido ?? SEM_CANAL,
      observacoes: tutor?.observacoes ?? '',
      endereco: enderecoParaFormulario(tutor?.endereco),
    });
  }, [aberto, tutor, reset]);

  const aoEnviar = handleSubmit(async (dados) => {
    // Campo em branco vira `null`, não string vazia. Na edição isso é o que
    // APAGA o valor — o formulário mostra o que está gravado, então o que a
    // pessoa vê é o que ela envia. O backend aceita null nos cinco opcionais.
    const opcional = (v: string) => (v.trim() ? v.trim() : null);

    const corpo = {
      nome: dados.nome,
      documento: opcional(dados.documento),
      email: opcional(dados.email),
      telefone: opcional(dados.telefone),
      canalPreferido:
        dados.canalPreferido === SEM_CANAL
          ? null
          : (dados.canalPreferido as (typeof CANAIS)[number]),
      observacoes: opcional(dados.observacoes),
      endereco: enderecoParaEnvio(dados.endereco),
    };

    try {
      if (tutor) {
        const atualizado = await atualizar.mutateAsync({ id: tutor.id, data: corpo });
        aoAtualizar?.(atualizado);
        await queryClient.invalidateQueries({
          queryKey: getTutoresControllerObterQueryKey(tutor.id),
        });
        // Listagem, detalhe e ficha do paciente mostram `tutorNome`. A chave
        // das consultas de paciente começa pela URL, então o prefixo cobre todas.
        await queryClient.invalidateQueries({
          predicate: (q) => String(q.queryKey[0]).startsWith('/v1/pacientes'),
        });
      } else {
        const criado = await criar.mutateAsync({ data: corpo });
        aoCriar?.(criado);
      }

      await queryClient.invalidateQueries({ queryKey: getTutoresControllerListarQueryKey() });
      toast.success(editando ? 'Responsável atualizado.' : 'Responsável cadastrado.');
      aoFechar();
    } catch (erro) {
      aplicarErrosDoServidor(erro, setError, 'Não foi possível salvar o responsável.');
    }
  });

  return (
    <Dialog open={aberto} onOpenChange={(v) => !v && aoFechar()}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{editando ? 'Editar responsável' : 'Novo responsável'}</DialogTitle>
          <DialogDescription>
            Só o nome é obrigatório. Os demais campos podem ser completados depois.
          </DialogDescription>
        </DialogHeader>

        {/* ⚠️ `stopPropagation`: o diálogo abre por dentro do formulário do
            paciente, e o evento do React atravessa o portal. Sem isto, salvar o
            responsável também submetia o paciente de trás. */}
        <form
          onSubmit={(e) => {
            e.stopPropagation();
            void aoEnviar(e);
          }}
          onKeyDown={bloquearEnvioPorEnter}
          className="space-y-4"
        >
          {errors.root && (
            <Alert variant="destructive">
              <AlertDescription>{errors.root.message}</AlertDescription>
            </Alert>
          )}

          <Campo id={idDe('nome')} rotulo="Nome" obrigatorio erro={errors.nome?.message}>
            <Input id={idDe('nome')} className={CLASSE_CONTROLE} {...register('nome')} />
          </Campo>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Campo id={idDe('documento')} rotulo="Documento" erro={errors.documento?.message}>
              <Input
                id={idDe('documento')}
                className={CLASSE_CONTROLE}
                {...register('documento')}
              />
            </Campo>

            <Campo
              id={idDe('telefone')}
              rotulo="Telefone"
              erro={errors.telefone?.message}
              dica="Usado no compartilhamento por WhatsApp."
            >
              {/* `Controller` e não `register`: o campo reescreve o valor a
                  cada tecla (máscara) e devolve E.164, não o que foi digitado. */}
              <Controller
                name="telefone"
                control={control}
                render={({ field }) => (
                  <CampoDeTelefone
                    id={idDe('telefone')}
                    valor={field.value}
                    aoMudar={field.onChange}
                    aoSair={field.onBlur}
                  />
                )}
              />
            </Campo>
          </div>

          <Campo id={idDe('email')} rotulo="E-mail" erro={errors.email?.message}>
            <Input
              id={idDe('email')}
              type="email"
              className={CLASSE_CONTROLE}
              {...register('email')}
            />
          </Campo>

          <Campo
            id={idDe('canalPreferido')}
            rotulo="Canal preferido"
            erro={errors.canalPreferido?.message}
            dica="Como este responsável prefere ser contatado."
          >
            <Controller
              name="canalPreferido"
              control={control}
              render={({ field }) => (
                <Select value={field.value} onValueChange={field.onChange}>
                  <SelectTrigger
                    id={idDe('canalPreferido')}
                    className={CLASSE_CONTROLE}
                    aria-label="Canal preferido"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={SEM_CANAL}>Não informado</SelectItem>
                    {CANAIS.map((canal) => (
                      <SelectItem key={canal} value={canal}>
                        {ROTULO_CANAL[canal] ?? canal}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            />
          </Campo>

          <Campo id={idDe('observacoes')} rotulo="Observações" erro={errors.observacoes?.message}>
            <Input
              id={idDe('observacoes')}
              className={CLASSE_CONTROLE}
              {...register('observacoes')}
            />
          </Campo>

          <fieldset className="space-y-3 border-t border-border pt-4">
            <legend className="text-sm font-semibold text-foreground">Endereço</legend>
            <p className="text-xs text-muted-foreground">
              Opcional. Sai no receituário de controle especial; em branco, a linha fica para preencher à mão.
            </p>
            <CamposDeEndereco control={control} setValue={setValue} erros={errors.endereco} idDe={(campo) => idDe(`endereco-${campo}`)} />
          </fieldset>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={aoFechar}>
              Cancelar
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {editando ? 'Salvar' : 'Cadastrar'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
